// src/components/BundleDetail.jsx

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../AuthContext';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Button,
    CircularProgress,
    Grid,
    Chip,
    Avatar,
    Tooltip,
    IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';
import { formatDateWithSuffix } from '../utils/formatDate';
import { formatTimeToAMPM } from '../utils/formatTime';
import { formatDuration } from '../utils/formatDuration';
import Platform from '../Platform/Platform';
import ShareButton from '../Share/ShareButton';

const API_URL = process.env.REACT_APP_API_URL;

function MyPostBundleDetails({ }) {
    const { userId } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();

    const postId = id
    // '6ca01b18-c460-482c-b548-2bd8c8fa1177'; // Replace as needed

    const [post, setPost] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [highlight, setHighlight] = useState(null);
    const [userData,setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [loadingSeconds, setLoadingSeconds] = useState(0);

    useEffect(() => {
              let timer;
            
              if (loading) {
                setLoadingSeconds(0); // reset timer
                timer = setInterval(() => {
                  setLoadingSeconds(prev => prev + 1);
                }, 1000);
              } else {
                clearInterval(timer);
                setLoadingSeconds(0); // optional reset after done
              }
            
              return () => clearInterval(timer); // cleanup on unmount or loading = false
            }, [loading]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/bundles/?post_id=${postId}`);
                if (!res.ok) throw new Error('Failed to fetch');
                const data = await res.json();
                setPost(data.post);
                setUserData(data.user);
                setSessions(data.sessions);
                setHighlight(data.post); // Set default highlighted item to bundle
            } catch (err) {
                console.error(err);
                setError('Failed to load bundle');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [postId]);

    const handleSessionClick = (session) => {
        setHighlight(session);
    };

    const handleBackToBundle = () => {
        setHighlight(post);
    };

    if (loading) {
        return (
            <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(3px)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress
            size={60}
            thickness={6}
            sx={{
              color: 'transparent',
              '& .MuiCircularProgress-circle': {
                stroke: 'url(#better-gradient)',
              },
            }}
          />

          {/* Gradient definition */}
          <svg width="0" height="0">
            <defs>
              <linearGradient id="better-gradient" x1="1" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f30b96ff" />
                <stop offset="50%" stopColor="#221ce0ff" />
                <stop offset="100%" stopColor="#0eacebff" />
              </linearGradient>
            </defs>
          </svg>

          <Typography
            variant="h6"
            sx={{
              mt: 2,
              color: '#fff',
              textShadow: '0 1px 3px rgba(0,0,0,0.7)',
            }}
          >
            Loading Sessions Details... ({loadingSeconds} sec)
          </Typography>
        </Box>
      </Box>
        );
    }

    if (error || !post) {
        return (
            <Box sx={{ textAlign: 'center', mt: 6 }}>
                <Typography color="error">{error || 'No data available'}</Typography>
            </Box>
        );
    }

    return (
        <div>
            <Box
                                              sx={{
                                                position: 'fixed', // or 'relative' / 'sticky' based on your layout
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                zIndex: 1300, // higher than MUI drawers (default is 1200)
                                              }}
                                            >
                                              <Header1 />
                                            </Box>
            <Box sx={{ maxWidth: '1200px', mx: 'auto', p: 3, mt:7 }}>
                

                <Card
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: ['column', 'row'],
                        mb: 5,
                        borderRadius: 3,
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: 5,
                        overflow: 'hidden',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: 6, right: 6 }}>
                            <ShareButton postUrl={`${window.location.origin}/share/bundle/${id}`} />
                        </Box>
                    {highlight.media_url && (
                        <CardMedia
                            component={highlight.media_type === 'video' ? 'video' : 'img'}
                            src={highlight.media_url}
                            controls
                            sx={{ width: ['100%', '60%'], height: 700, objectFit: 'cover' }}
                        />
                    )}
                    <CardContent sx={{ flex: 1, p: 4 }}>
                        {highlight.id !== post.id && (
                            <IconButton onClick={handleBackToBundle} sx={{ mb: 1 }}>
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <Typography variant="h4" gutterBottom fontWeight={600}>
                            {highlight.session_title || highlight.title || 'Highlighted Session'}
                        </Typography>
                        <Grid container spacing={2} justifyContent="left" sx={{ mt: 1, mb: 5 }}>

                            <Box
                                              sx={{
                                                  position: 'relative',
                                                  display: 'flex',
                                                  flexDirection: 'row',
                                                  alignItems: 'center',
                                                  gap: 2, // replaces manual margin for spacing
                                                  pt: '1rem',
                                              }}
                                          >
                                              
              
                                              <Avatar
                                                                                          sx={{ width: 50, height: 50, bgcolor: '#0a2e57ff', fontSize: 28 }}
                                                                                          src={userData.image || undefined}
                                                                                        >
                                                                                          {!userData.image && userData.name
                                                                                            ? userData.name.charAt(0).toUpperCase()
                                                                                            : null}
                                                                                        </Avatar>
                                              
              
                                              <Tooltip title="Click to view" placement="bottom">
                                                  <Typography
                  variant="h6"
                  onClick={() => navigate(`/expert-profile?expert_id=${userData.expert_id}`)}
                  sx={{
                      fontWeight: 'bold',
                      color: '#fff',
                      fontFamily: "'Helvetica', sans-serif",
                      letterSpacing: '0.15em',
                      fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
                      cursor: 'pointer',
                      transition: 'color 0.3s ease-in-out',
                      '&:hover': {
                          color: '#1E90FF',
                      },
                  }}
              >
                  {userData.name || 'Name'} {userData.surname || 'Surname'}
              </Typography>
              
                                              </Tooltip>
                                          </Box>

                            {/* Date & Time */}
                            {highlight.selectedDate && highlight.startTime && (
                                <Grid item>
                                    <Typography
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            color: '#ffffff',
                                            background: 'linear-gradient(135deg, #2079c2ff 0%, #1f2c33ff 100%)',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '18px',
                                            boxShadow: '0 2px 10px rgba(48, 71, 83, 0.4)',
                                            whiteSpace: 'nowrap',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                            letterSpacing: 0.4,
                                            userSelect: 'none',
                                            width: 'fit-content',
                                            cursor: 'default',
                                        }}
                                    >
                                        🗓 <span>{formatTimeToAMPM(highlight.startTime)} • {formatDateWithSuffix(highlight.selectedDate)}</span>
                                    </Typography>
                                </Grid>
                            )}


                            {/* Language */}
                            {highlight.language && (
                                <Grid item>
                                    <Typography
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            color: '#ffffff',
                                            background: 'linear-gradient(135deg, #c78b1cff, #27231aff)',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '20px',
                                            boxShadow: '0 0 10px rgba(160, 124, 45, 0.3)',
                                            letterSpacing: 0.3,
                                            whiteSpace: 'nowrap',
                                            textShadow: '0 0 4px rgba(0,0,0,0.25)',
                                            userSelect: 'none',
                                            cursor: 'default',
                                            width: 'fit-content',
                                        }}
                                    >
                                        🗣 {highlight.language}
                                    </Typography>
                                </Grid>
                            )}

                            {/* Price */}
                            {highlight.price && (
                                <Grid item>
                                    <Typography
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            color: '#ffffff',
                                            background: 'linear-gradient(135deg, #bcc71cff, #27231aff)',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '20px',
                                            boxShadow: '0 0 10px rgba(160, 124, 45, 0.3)',
                                            letterSpacing: 0.3,
                                            whiteSpace: 'nowrap',
                                            textShadow: '0 0 4px rgba(0,0,0,0.25)',
                                            userSelect: 'none',
                                            cursor: 'default',
                                            width: 'fit-content',
                                        }}
                                    >
                                        ₹{highlight.price}
                                    </Typography>
                                </Grid>
                            )}

                        </Grid>
                        {/* Description */}
                        <Grid item xs={12}>
                            <Typography
                                sx={{
                                    background: 'rgba(255, 255, 255, 0.05)',
                                    borderLeft: '4px solid #1976d2',
                                    padding: 2,
                                    borderRadius: '8px',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6,
                                    color: '#e0e0e0',
                                    boxShadow: 'inset 0 0 5px rgba(255,255,255,0.05)',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {highlight.description || 'No description provided.'}
                            </Typography>
                        </Grid>
                        <Button
                            variant="contained"
                            color="primary"
                            endIcon={<PlayCircleOutlineIcon />}
                            sx={{ mt: 2 }}
                            disabled={!highlight.host_meeting_link}
                            onClick={() => {
                                if (highlight.host_meeting_link) {
                                    window.open(highlight.host_meeting_link, '_blank');
                                }
                            }}
                        >
                            Join Session
                        </Button>

                        

                    </CardContent>
                </Card>

                {/* Session List */}
                <Typography variant="h5" gutterBottom fontWeight={500}>
                    Sessions
                </Typography>

                <Grid container spacing={3}>
                    {sessions.map((session) => (
                        <Grid item xs={12} sm={6} md={4} key={session.id}>
                            <Card
                                onClick={() => handleSessionClick(session)}
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 3,
                                    background: 'rgba(255, 255, 255, 0.04)',
                                    boxShadow: 3,
                                    cursor: 'pointer',
                                    transition: 'transform 0.3s',
                                    '&:hover': {
                                        transform: 'scale(1.03)',
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                {session.media_url && (
                                    <CardMedia
                                        component={session.media_type === 'video' ? 'video' : 'img'}
                                        src={session.media_url}
                                        controls
                                        sx={{ height: 180, objectFit: 'cover' }}
                                    />
                                )}
                                <CardContent sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" gutterBottom fontWeight={600}>
                                        {session.session_title || 'Untitled Session'}
                                    </Typography>
                                    <Grid container spacing={2} justifyContent="left" sx={{ mt: 1, mb: 5 }}>
                                                      {/* Date + Time + Duration */}
                                    {session.selectedDate && session.startTime && session.duration && (
                                      <Grid item>
                                        <Typography
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontWeight: '600',
                                            fontSize: '1rem',
                                            color: '#ffffff',
                                            background: 'linear-gradient(135deg, #2079c2ff 0%, #1f2c33ff 100%)',
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '18px',
                                            boxShadow: '0 2px 10px rgba(48, 71, 83, 0.4)',
                                            whiteSpace: 'nowrap',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
                                            letterSpacing: 0.4,
                                            userSelect: 'none',
                                            width: 'fit-content',
                                            cursor: 'default',
                                          }}
                                        >
                                          🗓
                                          <span>
                                            {formatTimeToAMPM(session.startTime)} • {formatDateWithSuffix(session.selectedDate)} • {formatDuration(session.duration)}
                                          </span>
                                        </Typography>
                                      </Grid>
                                    )}
                                    </Grid>
                                    
                                                     
                                                      {/* Session Description */}
                                    <Grid item xs={12}>
                                      <Typography
                                        sx={{
                                          background: 'rgba(255, 255, 255, 0.04)',
                                          borderLeft: '4px solid #4caf50',
                                          padding: 2,
                                          borderRadius: '8px',
                                          fontSize: '0.95rem',
                                          lineHeight: 1.6,
                                          color: '#dddddd',
                                          boxShadow: 'inset 0 0 6px rgba(0,0,0,0.1)',
                                          whiteSpace: 'normal',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {session.description?.substring(0, 120) || 'No description available.'}
                                        {session.description && session.description.length > 120 && '...'}
                                      </Typography>
                                    </Grid>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        endIcon={<PlayCircleOutlineIcon />}
                                        sx={{ mt: 2 }}
                                        onClick={() => {
                                            if (session?.host_meeting_link) {
                                                window.open(session.host_meeting_link, '_blank', 'noopener,noreferrer');
                                            }
                                        }}
                                        disabled={!session?.host_meeting_link}
                                    >
                                        Join Session
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

            </Box>
            <Footer2 />
        </div>
    );
}

export default MyPostBundleDetails;
